/**
 * LogModule — Iter 209 Part 2 (Frontend).
 *
 * Shared "Log" component (previously "RAID") used from four scopes:
 *   • workspace   → GET /workspaces/{ws}/log        (all log items)
 *   • client      → GET /workspaces/{ws}/clients/{cid}/log
 *   • brand       → GET /workspaces/{ws}/brands/{bid}/log
 *   • project     → GET /projects/{pid}/raid        (project-scoped, existing)
 *
 * Writes always go through:
 *   POST /workspaces/{ws}/log                (create)
 *   PATCH /raid/{id}                          (update)
 *   DELETE /raid/{id}                         (delete)
 *   POST  /log/{id}/create-task               (spawn a task)
 *   POST  /workspaces/{ws}/log/bulk-email     (bulk email)
 *   GET / POST /log/{id}/comments             (comment thread)
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import SmartRichText from "@/components/SmartRichText";
import AdvancedTable from "@/components/table/AdvancedTable";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import { Plus, Trash2, Mail, ListChecks, MessageSquare, Send, X, Filter, AlertTriangle, MessageCircle, Sparkles, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export const LOG_KINDS = [
  { v: "risk",            label: "Risk",            cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30" },
  { v: "action",          label: "Action",          cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30" },
  { v: "issue",           label: "Issue",           cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  { v: "concern",         label: "Concern",         cls: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30" },
  { v: "information",     label: "Information",     cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30" },
  { v: "decision",        label: "Decision",        cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  { v: "constraint",      label: "Constraint",      cls: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30" },
  { v: "change",          label: "Change",          cls: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30" },
  { v: "lesson_learned",  label: "Lesson Learned",  cls: "bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/30" },
  { v: "opportunity",     label: "Opportunity",     cls: "bg-lime-500/15 text-lime-700 dark:text-lime-300 border-lime-500/30" },
  { v: "status_update",   label: "Status Update",   cls: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" },
  // R74 · Accountability-first entry types
  { v: "suggestion",      label: "Suggestion",      cls: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30" },
  { v: "recommendation",  label: "Recommendation",  cls: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30" },
];

export const LOG_STATUSES = ["open", "in_progress", "resolved", "closed", "watching"];
export const LOG_SEVERITIES = ["low", "medium", "high", "critical"];
// R74 · Decision outcome — used on suggestions/recommendations/actions to
// track whether the item was ultimately implemented or ignored.
export const LOG_OUTCOMES = [
  { v: "pending",     label: "Pending" },
  { v: "accepted",    label: "Accepted" },
  { v: "rejected",    label: "Rejected" },
  { v: "deferred",    label: "Deferred" },
  { v: "implemented", label: "Implemented" },
  { v: "superseded",  label: "Superseded" },
];

export function KindChip({ kind }) {
  const k = LOG_KINDS.find((x) => x.v === kind) || { label: kind, cls: "" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-widest ${k.cls}`}
          data-testid={`log-kind-chip-${kind}`}>
      {k.label}
    </span>
  );
}

/**
 * Props:
 *   scope:     "workspace" | "client" | "brand" | "project"
 *   scopeId:   client_id / brand_id / project_id (ignored when workspace)
 *   defaults:  optional { project_id, client_id, brand_id } used for new-item pre-fill
 *   testid:    prefix for stable data-testid attributes
 */
export default function LogModule({ scope = "workspace", scopeId, defaults = {}, testid = "log" }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  // R74 · AI project assessment dialog
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  useEffect(() => {
    if (!assessmentOpen || assessmentData || assessmentLoading) return;
    (async () => {
      setAssessmentLoading(true);
      try {
        const { data } = await api.post(`/projects/${scopeId}/log/ai-assessment`);
        setAssessmentData(data);
      } catch (e) {
        toast.error(apiErrorMessage(e, "Assessment failed"));
        setAssessmentOpen(false);
      } finally { setAssessmentLoading(false); }
    })();
  }, [assessmentOpen, assessmentData, assessmentLoading, scopeId]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);

  const listUrl = useMemo(() => {
    if (!activeWorkspaceId) return null;
    if (scope === "client" && scopeId) return `/workspaces/${activeWorkspaceId}/clients/${scopeId}/log`;
    if (scope === "brand" && scopeId)  return `/workspaces/${activeWorkspaceId}/brands/${scopeId}/log`;
    if (scope === "project" && scopeId) return `/workspaces/${activeWorkspaceId}/log?project_id=${scopeId}`;
    return `/workspaces/${activeWorkspaceId}/log`;
  }, [activeWorkspaceId, scope, scopeId]);

  const load = async () => {
    if (!listUrl) return;
    setBusy(true);
    try {
      const params = [];
      if (kind) params.push(`kind=${encodeURIComponent(kind)}`);
      if (status) params.push(`status=${encodeURIComponent(status)}`);
      const sep = listUrl.includes("?") ? "&" : "?";
      const url = params.length ? `${listUrl}${sep}${params.join("&")}` : listUrl;
      const { data } = await api.get(url);
      setRows(data?.rows || data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load logs")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [listUrl, kind, status]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/projects`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/clients`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
    ]).then(([m, p, c, b]) => {
      setMembers(m.data || []);
      setProjects(p.data || []);
      setClients(c.data || []);
      setBrands(b.data || []);
    });
  }, [activeWorkspaceId]);

  const counts = useMemo(() => {
    const c = {};
    LOG_KINDS.forEach((k) => c[k.v] = 0);
    rows.forEach((r) => { if (c[r.kind] != null) c[r.kind] += 1; });
    return c;
  }, [rows]);

  const columns = useMemo(() => ([
    {
      key: "title", label: "Item", width: 260, sortable: true, filterType: "text", frozen: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold truncate">{r.title}</div>
          {r.last_comment_body && (
            <div className="text-[10px] text-meta truncate">💬 {r.last_comment_by_name}: {r.last_comment_body?.replace(/<[^>]+>/g, "").slice(0, 80)}</div>
          )}
        </div>
      ),
    },
    {
      key: "description", label: "Description", width: 320, sortable: false, filterType: "text",
      accessor: (r) => (r.description || "").replace(/<[^>]+>/g, " "),
      render: (r) => {
        const txt = (r.description || "").replace(/<[^>]+>/g, " ").trim();
        return txt
          ? <span className="text-xs text-muted-foreground line-clamp-2" title={txt}>{txt}</span>
          : <span className="text-meta">—</span>;
      },
      exportValue: (r) => (r.description || "").replace(/<[^>]+>/g, " ").trim(),
    },
    {
      key: "kind", label: "Kind", width: 130, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: LOG_KINDS.map((k) => ({ value: k.v, label: k.label })),
      render: (r) => <KindChip kind={r.kind} />,
    },
    {
      key: "status", label: "Status", width: 110, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: LOG_STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") })),
      render: (r) => <span className="chip text-[10px]">{(r.status || "open").replace("_", " ")}</span>,
    },
    {
      key: "severity", label: "Severity", width: 90, sortable: true, groupable: true,
      filterType: "select",
      filterOptions: LOG_SEVERITIES.map((s) => ({ value: s, label: s })),
      render: (r) => r.severity ? <span className="chip text-[10px]">{r.severity}</span> : <span className="text-meta">—</span>,
    },
    {
      key: "client_name", label: "Client", width: 140, sortable: true, groupable: true, filterType: "text",
      render: (r) => r.client_name
        ? <span className="chip text-[10px] bg-indigo-500/10 text-indigo-700 dark:text-indigo-300">{r.client_name}</span>
        : <span className="text-meta">—</span>,
    },
    {
      key: "brand_name", label: "Brand", width: 140, sortable: true, groupable: true, filterType: "text",
      render: (r) => r.brand_name
        ? <span className="chip text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">{r.brand_name}</span>
        : <span className="text-meta">—</span>,
    },
    {
      key: "project_name", label: "Project", width: 160, sortable: true, groupable: true, filterType: "text",
      render: (r) => r.project_name
        ? <span className="chip text-[10px]">{r.project_name}</span>
        : <span className="text-meta">—</span>,
    },
    {
      key: "assignee_name", label: "Assignee", width: 140, sortable: true, groupable: true, filterType: "text",
      accessor: (r) => r.assignee?.name || "",
      render: (r) => r.assignee?.name
        ? <span className="text-xs">{r.assignee.name}</span>
        : <span className="text-meta">Unassigned</span>,
    },
    {
      key: "due_date", label: "Due", width: 110, sortable: true, filterType: "date-range",
      accessor: (r) => r.due_date || "",
      render: (r) => r.due_date ? <span className="chip text-[10px]">{String(r.due_date).slice(0, 10)}</span> : <span className="text-meta">—</span>,
    },
    {
      key: "comments_count", label: "💬", width: 60, sortable: true, align: "right", type: "number",
      accessor: (r) => r.comments_count || 0,
    },
    {
      key: "created_at", label: "Logged", width: 110, sortable: true, align: "right", filterType: "date-range",
      accessor: (r) => r.created_at,
      render: (r) => <span className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</span>,
    },
  ]), []);

  return (
    <div className="space-y-4" data-testid={`${testid}-module`}>
      {/* Kind chips row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-11 gap-1.5" data-testid={`${testid}-kind-strip`}>
        {LOG_KINDS.map((k) => (
          <button
            key={k.v}
            type="button"
            onClick={() => setKind((cur) => cur === k.v ? "" : k.v)}
            className={`border px-2 py-1.5 text-left transition ${kind === k.v ? "border-foreground bg-muted/50" : "border-border hover:border-foreground/40"}`}
            data-testid={`${testid}-kind-btn-${k.v}`}
          >
            <div className={`inline-block text-[9px] px-1.5 py-0.5 border ${k.cls}`}>{k.label}</div>
            <div className="text-xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>{counts[k.v] || 0}</div>
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="border border-border bg-muted/30 p-2.5 flex flex-wrap items-center gap-2" data-testid={`${testid}-filter-bar`}>
        <Filter className="w-3.5 h-3.5 text-foreground" />
        <Select value={kind || "_all"} onValueChange={(v) => setKind(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`${testid}-filter-kind`}><SelectValue placeholder="All kinds" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All kinds</SelectItem>
            {LOG_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid={`${testid}-filter-status`}><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All statuses</SelectItem>
            {LOG_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-meta">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
        <div className="ml-auto flex items-center gap-2">
          {/* R74 · AI project assessment — only visible when Log is scoped
              to a project so directors can generate a risk & accountability
              report from the current log entries + status updates. */}
          {scope === "project" && scopeId && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-none h-8"
              onClick={() => setAssessmentOpen(true)}
              data-testid={`${testid}-ai-assess-btn`}
              title="Generate an AI risk & accountability assessment from this project's log"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Assessment
            </Button>
          )}
          <Button size="sm" className="rounded-none h-8" onClick={() => setEditing({})} data-testid={`${testid}-new-btn`}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New log entry
          </Button>
        </div>
      </div>

      {/* List */}
      <AdvancedTable
        tableKey={`${testid}-table`}
        title="Log entries"
        rows={rows}
        columns={columns}
        loading={busy && rows.length === 0}
        emptyIcon={AlertTriangle}
        emptyTitle="No log entries"
        emptyDescription="Nothing has been logged yet. Click 'New log entry' to add the first one."
        onRowClick={(r) => setEditing(r)}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        renderBulkBar={(rowsSel) => (
          <div className="flex items-center gap-2">
            <span className="text-xs">{rowsSel.length} selected</span>
            <Button size="sm" className="rounded-none h-7" onClick={() => setBulkEmailOpen(true)} data-testid={`${testid}-bulk-email-btn`}>
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Email selected
            </Button>
          </div>
        )}
      />

      {editing && (
        <LogDrawer
          row={editing}
          scope={scope}
          scopeId={scopeId}
          defaults={defaults}
          members={members}
          projects={projects}
          clients={clients}
          brands={brands}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          testid={testid}
        />
      )}
      {bulkEmailOpen && (
        <BulkEmailDialog
          logIds={Array.from(selected)}
          rows={rows.filter((r) => selected.has(r.id))}
          members={members}
          onClose={() => setBulkEmailOpen(false)}
          onSent={() => { setBulkEmailOpen(false); setSelected(new Set()); toast.success("Bulk email dispatched"); }}
          testid={testid}
        />
      )}
      {/* R74 · AI Project Assessment dialog */}
      {assessmentOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) { setAssessmentOpen(false); setAssessmentData(null); } }}>
          <DialogContent className="w-[95vw] max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid={`${testid}-ai-assessment-dialog`}>
            <DialogHeader>
              <DialogTitle className="text-sm inline-flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-primary" />
                AI Project Assessment
              </DialogTitle>
            </DialogHeader>
            {assessmentLoading || !assessmentData ? (
              <div className="text-sm text-muted-foreground py-6 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Analysing log, updates and milestones…
              </div>
            ) : (
              <AssessmentView data={assessmentData.assessment || {}} inputs={assessmentData.input_counts || {}} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// R74 · Small labelled section wrapper used inside AssessmentView.
function AssessmentSection({ title, children, testid }) {
  return (
    <section className="border-t border-border pt-3 mt-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-700 mb-1.5">{title}</div>
      {children}
    </section>
  );
}

// R74 · Renders the structured AI project assessment: overall health,
// headline, unimplemented recommendations, delayed action items, risks,
// accountability gaps, cost narrative, recommended next steps.
function AssessmentView({ data, inputs }) {
  const health = data.overall_health || "unknown";
  const healthCls = {
    on_track: "border-emerald-500 text-emerald-800 bg-emerald-50",
    at_risk: "border-amber-500 text-amber-800 bg-amber-50",
    off_track: "border-rose-500 text-rose-800 bg-rose-50",
    blocked: "border-red-600 text-red-800 bg-red-50",
    unknown: "border-neutral-400 text-neutral-700 bg-neutral-50",
  }[health] || "border-neutral-400";
  const Section = AssessmentSection;
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center flex-wrap gap-2">
        <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border ${healthCls}`}
                data-testid="ai-assessment-health">
          {String(health).replace("_", " ")}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {(inputs.logs || 0)} logs · {(inputs.updates || 0)} updates · {(inputs.milestones || 0)} milestones
        </span>
      </div>
      {data.headline && (
        <p className="text-sm font-medium text-neutral-900" data-testid="ai-assessment-headline">{data.headline}</p>
      )}
      {Array.isArray(data.unimplemented_recommendations) && data.unimplemented_recommendations.length > 0 && (
        <Section title={`Unimplemented recommendations (${data.unimplemented_recommendations.length})`} testid="ai-assessment-unimplemented">
          <ul className="space-y-1.5">
            {data.unimplemented_recommendations.map((r, i) => (
              <li key={r.id || i} className="border-l-2 border-fuchsia-500 pl-2 text-xs">
                <div className="font-medium">{r.title}</div>
                <div className="text-[10px] text-neutral-700">
                  {r.aged_days != null && <>aged {r.aged_days}d · </>}
                  {r.assigned_to && <>assigned to <strong>{r.assigned_to}</strong> · </>}
                  {r.likely_impact_if_ignored && <em>{r.likely_impact_if_ignored}</em>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {Array.isArray(data.delayed_action_items) && data.delayed_action_items.length > 0 && (
        <Section title={`Delayed action items (${data.delayed_action_items.length})`} testid="ai-assessment-delayed">
          <ul className="space-y-1.5">
            {data.delayed_action_items.map((r, i) => (
              <li key={r.id || i} className="border-l-2 border-rose-500 pl-2 text-xs">
                <div className="font-medium">{r.title}</div>
                <div className="text-[10px] text-neutral-700">
                  {r.owner && <>owner <strong>{r.owner}</strong> · </>}
                  {r.days_late != null && <>{r.days_late}d late · </>}
                  {r.estimated_cost_of_delay && <>impact <strong>{r.estimated_cost_of_delay}</strong> · </>}
                  {r.why && <em>{r.why}</em>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {Array.isArray(data.top_risks) && data.top_risks.length > 0 && (
        <Section title="Top risks" testid="ai-assessment-risks">
          <ul className="space-y-1.5">
            {data.top_risks.map((r, i) => (
              <li key={r.id || i} className="border-l-2 border-red-500 pl-2 text-xs">
                <div className="font-medium">{r.title}</div>
                <div className="text-[10px] text-neutral-700">
                  severity <strong>{r.severity}</strong> · mitigation <strong>{r.mitigation_present || "?"}</strong>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {Array.isArray(data.accountability_gaps) && data.accountability_gaps.length > 0 && (
        <Section title="Accountability gaps" testid="ai-assessment-gaps">
          <ul className="space-y-1.5">
            {data.accountability_gaps.map((r, i) => (
              <li key={i} className="border-l-2 border-amber-500 pl-2 text-xs">
                <div className="text-[10px] text-neutral-700">
                  <strong>{r.who || "—"}</strong> — {r.what}
                  {r.gap_type && <> · <em>{r.gap_type.replace(/_/g, " ")}</em></>}
                  {r.days != null && <> · {r.days}d</>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      {Array.isArray(data.cost_of_inaction_narrative) && data.cost_of_inaction_narrative.length > 0 && (
        <Section title="Cost of inaction" testid="ai-assessment-cost">
          <ul className="list-disc pl-5 text-xs space-y-1">
            {data.cost_of_inaction_narrative.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </Section>
      )}
      {Array.isArray(data.recommended_next_steps) && data.recommended_next_steps.length > 0 && (
        <Section title="Recommended next steps" testid="ai-assessment-next">
          <ol className="list-decimal pl-5 text-xs space-y-1">
            {data.recommended_next_steps.map((s, i) => (
              <li key={i}>
                <strong>{s.title}</strong>
                {s.owner_suggestion && <> — {s.owner_suggestion}</>}
                {s.urgency && <> <span className="uppercase text-[9px] tracking-widest ml-1">[{s.urgency}]</span></>}
                {s.why && <div className="text-neutral-700">{s.why}</div>}
              </li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  );
}


/* ─── Drawer / editor ────────────────────────────────────────────── */
function LogDrawer({ row, scope, scopeId, defaults, members, projects, clients, brands, onClose, onSaved, testid }) {
  const { activeWorkspaceId } = useAuth();
  const isNew = !row?.id;
  const [form, setForm] = useState(() => ({
    kind: row?.kind || "risk",
    title: row?.title || "",
    description: row?.description || "",
    severity: row?.severity || "medium",
    status: row?.status || "open",
    due_date: row?.due_date ? String(row.due_date).slice(0, 10) : "",
    assignee_id: row?.assignee_id || "",
    owner_id: row?.owner_id || "",
    project_id: row?.project_id || defaults.project_id || (scope === "project" ? scopeId : ""),
    brand_id: row?.brand_id || defaults.brand_id || (scope === "brand" ? scopeId : ""),
    client_id: row?.client_id || defaults.client_id || (scope === "client" ? scopeId : ""),
    is_external_visible: !!row?.is_external_visible,
    // R74 · Accountability fields
    external_assignee_name: row?.external_assignee_name || "",
    external_assignee_email: row?.external_assignee_email || "",
    decision_outcome: row?.decision_outcome || "",
    consequence: row?.consequence || "",
    notes: row?.notes || "",
  }));
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const body = {
        kind: form.kind,
        title: form.title.trim(),
        description: form.description || null,
        severity: form.severity || null,
        status: form.status,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || null,
        owner_id: form.owner_id || null,
        project_id: form.project_id || null,
        brand_id: form.brand_id || null,
        client_id: form.client_id || null,
        is_external_visible: !!form.is_external_visible,
        // R74
        external_assignee_name: form.external_assignee_name || null,
        external_assignee_email: form.external_assignee_email || null,
        decision_outcome: form.decision_outcome || null,
        consequence: form.consequence || null,
        notes: form.notes || null,
      };
      let saved;
      if (isNew) {
        const { data } = await api.post(`/workspaces/${activeWorkspaceId}/log`, body);
        saved = data;
        toast.success("Log entry created");
      } else {
        // PATCH via legacy /raid/{id} which accepts these fields
        const patch = { ...body };
        delete patch.kind; delete patch.project_id; delete patch.client_id;
        const { data } = await api.patch(`/raid/${row.id}`, patch);
        saved = data;
        toast.success("Log entry updated");
      }
      onSaved?.(saved);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save")); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!row?.id) { onClose(); return; }
    if (!window.confirm("Delete this log entry?")) return;
    try {
      await api.delete(`/raid/${row.id}`);
      toast.success("Deleted");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const spawnTask = async () => {
    if (!row?.id) { toast.error("Save the log first"); return; }
    try {
      const { data } = await api.post(`/log/${row.id}/create-task`, {
        title: `[${row.kind}] ${row.title}`,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
        priority: form.severity === "critical" ? "urgent" : form.severity === "high" ? "high" : "medium",
      });
      toast.success(`Task created: ${data.title || "OK"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to spawn task")); }
  };

  const pingAssignee = async () => {
    if (!row?.id) { toast.error("Save the log first"); return; }
    if (!form.assignee_id) { toast.error("No assignee to ping"); return; }
    if (!window.confirm("Send a WhatsApp ping to the assignee?")) return;
    try {
      await api.post(`/log/${row.id}/ping-assignee`);
      toast.success("Ping sent 🚀");
    } catch (e) { toast.error(apiErrorMessage(e, "Ping failed")); }
  };

  const filteredBrands = brands.filter((b) => !form.client_id || b.client_id === form.client_id);
  const filteredProjects = projects.filter((p) =>
    (!form.client_id || p.client_id === form.client_id) &&
    (!form.brand_id  || p.brand_id  === form.brand_id));

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto p-0" data-testid={`${testid}-drawer`}>
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="text-lg">{isNew ? "New log entry" : "Edit log entry"}</SheetTitle>
        </SheetHeader>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Kind</Label>
              <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-kind`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOG_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Status</Label>
              <Select value={form.status} onValueChange={(v) => upd("status", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-status`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOG_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Title</Label>
            <Input value={form.title} onChange={(e) => upd("title", e.target.value)} className="h-10 rounded-none" data-testid={`${testid}-drawer-title`} />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Description</Label>
            <SmartRichText
              value={form.description}
              onChange={(v) => upd("description", v)}
              minRows={4}
              maxHeight={360}
              mediaMode="url-only"
              workspaceId={activeWorkspaceId}
              placeholder="Rich description — use / for menu…"
              testid={`${testid}-drawer-desc`}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => upd("severity", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-severity`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOG_SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Due date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => upd("due_date", e.target.value)} className="h-10 rounded-none" data-testid={`${testid}-drawer-due`} />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Assignee</Label>
              <Select value={form.assignee_id || "_none"} onValueChange={(v) => upd("assignee_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-assignee`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {members.map((m) => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Client</Label>
              <Select value={form.client_id || "_none"} onValueChange={(v) => upd("client_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-client`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Brand</Label>
              <Select value={form.brand_id || "_none"} onValueChange={(v) => upd("brand_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-brand`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {filteredBrands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Project</Label>
              <Select value={form.project_id || "_none"} onValueChange={(v) => upd("project_id", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-project`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {filteredProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* R74 · External assignee + accountability outcome. Directors
              use these to hold client-side stakeholders answerable and
              track whether recommendations actually get implemented. */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 mt-3 border-t border-border">
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">External assignee (name)</Label>
              <Input
                value={form.external_assignee_name}
                onChange={(e) => upd("external_assignee_name", e.target.value)}
                placeholder="Client-side owner"
                className="h-10 rounded-none"
                data-testid={`${testid}-drawer-external-name`}
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">External assignee (email)</Label>
              <Input
                type="email"
                value={form.external_assignee_email}
                onChange={(e) => upd("external_assignee_email", e.target.value)}
                placeholder="name@client.com"
                className="h-10 rounded-none"
                data-testid={`${testid}-drawer-external-email`}
              />
            </div>
            <div>
              <Label className="label-eyebrow mb-1.5 inline-block">Decision outcome</Label>
              <Select value={form.decision_outcome || "_none"} onValueChange={(v) => upd("decision_outcome", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid={`${testid}-drawer-outcome`}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {LOG_OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Consequence if not implemented</Label>
            <Textarea
              value={form.consequence}
              onChange={(e) => upd("consequence", e.target.value)}
              placeholder="What will happen if nobody acts on this? (business impact, risk, cost, missed deadline…)"
              rows={3}
              className="rounded-none"
              data-testid={`${testid}-drawer-consequence`}
            />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => upd("notes", e.target.value)}
              placeholder="Free-form notes, follow-up history, context…"
              rows={3}
              className="rounded-none"
              data-testid={`${testid}-drawer-notes`}
            />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_external_visible} onChange={(e) => upd("is_external_visible", e.target.checked)} data-testid={`${testid}-drawer-external`} />
            Visible to external (clients)
          </label>

          <div className="flex items-center justify-between border-t border-border pt-4 mt-4">
            <div className="flex gap-2">
              {!isNew && (
                <Button variant="outline" className="rounded-none h-9 text-destructive" onClick={remove} data-testid={`${testid}-drawer-delete`}>
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              )}
              {!isNew && (
                <Button variant="outline" className="rounded-none h-9" onClick={spawnTask} data-testid={`${testid}-drawer-spawn-task`}>
                  <ListChecks className="w-4 h-4 mr-1.5" /> Create task
                </Button>
              )}
              {!isNew && form.assignee_id && (
                <Button variant="outline" className="rounded-none h-9" onClick={pingAssignee} data-testid={`${testid}-drawer-ping-wa`}
                        title="Send a WhatsApp ping to the assignee via Interakt">
                  <MessageCircle className="w-4 h-4 mr-1.5 text-emerald-600" /> Ping WhatsApp
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none h-9" onClick={onClose} data-testid={`${testid}-drawer-cancel`}>Cancel</Button>
              <Button className="rounded-none h-9" onClick={save} disabled={busy} data-testid={`${testid}-drawer-save`}>{busy ? "Saving…" : "Save"}</Button>
            </div>
          </div>

          {!isNew && <CommentPanel rid={row.id} testid={testid} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Comment thread panel (with @-mentions) ─────────────────────── */
function CommentPanel({ rid, testid }) {
  const { activeWorkspaceId } = useAuth();
  const [comments, setComments] = useState([]);
  const [busy, setBusy] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [members, setMembers] = useState([]);
  const [mentionQ, setMentionQ] = useState(null);   // {start, query} or null
  const [taRef, setTaRef] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/log/${rid}/comments`);
      setComments(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load comments")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [rid]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then((r) => setMembers(r.data || []))
      .catch(() => {});
  }, [activeWorkspaceId]);

  const post = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      await api.post(`/log/${rid}/comments`, { body: draft.trim() });
      setDraft("");
      setMentionQ(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to comment")); }
    finally { setPosting(false); }
  };
  const remove = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/log/${rid}/comments/${cid}`); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const onDraftChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    // Detect @… at caret
    const caret = e.target.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = before.match(/(^|\s)@([^\s@[\]()]{0,32})$/);
    if (m) {
      setMentionQ({ start: caret - m[2].length - 1, query: m[2].toLowerCase() });
    } else {
      setMentionQ(null);
    }
  };

  const insertMention = (mem) => {
    if (!mentionQ) return;
    const name = mem.name || mem.email || "user";
    const uid = mem.user_id || mem.id;
    const before = draft.slice(0, mentionQ.start);
    const afterStart = mentionQ.start + 1 /* @ */ + (mentionQ.query?.length || 0);
    const after = draft.slice(afterStart);
    const insertion = `@[${name}](${uid}) `;
    const next = before + insertion + after;
    setDraft(next);
    setMentionQ(null);
    setTimeout(() => {
      if (taRef) {
        const pos = (before + insertion).length;
        taRef.focus();
        taRef.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const suggestions = useMemo(() => {
    if (!mentionQ) return [];
    const q = (mentionQ.query || "").toLowerCase();
    return members.filter((m) =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [mentionQ, members]);

  // Comment body renderer — turn @[Name](uid) into a colored chip
  const renderBody = (body) => {
    const parts = [];
    let last = 0;
    const re = /@\[([^\]]+)\]\(([a-f0-9\-]{8,})\)/g;
    let m;
    let idx = 0;
    while ((m = re.exec(body)) !== null) {
      if (m.index > last) parts.push(body.slice(last, m.index));
      parts.push(
        <span key={`m-${idx++}`} className="text-primary font-semibold bg-primary/10 px-1 py-0.5 rounded-sm">@{m[1]}</span>
      );
      last = m.index + m[0].length;
    }
    if (last < body.length) parts.push(body.slice(last));
    return parts;
  };

  return (
    <div className="border-t border-border pt-4 mt-4" data-testid={`${testid}-comment-panel`}>
      <div className="flex items-center gap-2 mb-3 label-eyebrow text-xs">
        <MessageSquare className="w-3.5 h-3.5" /> Comments · {comments.length}
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {busy && <div className="text-xs text-meta">Loading…</div>}
        {!busy && comments.length === 0 && <div className="text-xs text-meta">No comments yet.</div>}
        {comments.map((c) => (
          <div key={c.id} className="border border-border p-2.5 text-xs" data-testid={`${testid}-comment-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{c.author_name}</span>
                <span className="text-meta text-[10px]">{fmtRelative(c.created_at)}</span>
                {(c.mentioned_user_ids || []).length > 0 && (
                  <span className="chip text-[9px] bg-primary/10 text-primary border-primary/30">🔔 {c.mentioned_user_ids.length}</span>
                )}
              </div>
              <button className="text-meta hover:text-destructive" onClick={() => remove(c.id)} data-testid={`${testid}-comment-del-${c.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="whitespace-pre-wrap">{renderBody(c.body)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 relative">
        <Textarea rows={3} value={draft} onChange={onDraftChange} placeholder="Add a comment…  type @ to mention teammates"
                  ref={setTaRef}
                  className="rounded-none text-xs" data-testid={`${testid}-comment-input`} />
        {mentionQ && suggestions.length > 0 && (
          <div className="absolute left-2 -top-2 -translate-y-full bg-popover border border-border shadow-lg z-10 min-w-[220px] max-h-56 overflow-auto"
               data-testid={`${testid}-mention-menu`}>
            {suggestions.map((mem) => (
              <button key={mem.user_id || mem.id} type="button"
                      onMouseDown={(e) => { e.preventDefault(); insertMention(mem); }}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                      data-testid={`${testid}-mention-opt-${(mem.user_id || mem.id).slice(0, 8)}`}>
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-semibold flex items-center justify-center">
                  {(mem.name || mem.email || "?").slice(0, 2).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="font-medium block truncate">{mem.name || mem.email}</span>
                  <span className="text-meta text-[10px] block truncate">{mem.email}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center mt-2">
          <span className="text-[10px] text-meta">Tip: type <kbd className="px-1 border">@</kbd> to mention · mentioned users get email + in-app ping</span>
          <Button size="sm" className="rounded-none h-8" onClick={post} disabled={posting || !draft.trim()} data-testid={`${testid}-comment-post`}>
            <Send className="w-3.5 h-3.5 mr-1.5" /> Post
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Bulk email dialog ─────────────────────────────────────────── */
function BulkEmailDialog({ logIds, rows, members, onClose, onSent, testid }) {
  const { activeWorkspaceId } = useAuth();
  const [freeText, setFreeText] = useState("");
  const [selectedMembers, setSelectedMembers] = useState(() => new Set());
  const [subject, setSubject] = useState(`Log update — ${logIds.length} item${logIds.length === 1 ? "" : "s"}`);
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleMember = (email) => {
    setSelectedMembers((prev) => {
      const n = new Set(prev);
      if (n.has(email)) n.delete(email); else n.add(email);
      return n;
    });
  };

  const recipients = useMemo(() => {
    const free = freeText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    return Array.from(new Set([...Array.from(selectedMembers), ...free]));
  }, [freeText, selectedMembers]);

  const send = async () => {
    if (recipients.length === 0) { toast.error("Pick at least one recipient"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/log/bulk-email`, {
        log_ids: logIds,
        recipients,
        subject: subject.trim() || undefined,
        intro: intro.trim() || undefined,
      });
      onSent?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk email failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid={`${testid}-bulk-email-dialog`}>
        <DialogHeader>
          <DialogTitle>Email {logIds.length} log item{logIds.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Workspace members</Label>
            <div className="max-h-40 overflow-y-auto border border-border p-2 space-y-1">
              {members.length === 0 && <div className="text-xs text-meta">No members loaded.</div>}
              {members.map((m) => {
                const email = m.email || "";
                if (!email) return null;
                return (
                  <label key={m.user_id || m.id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={selectedMembers.has(email)} onChange={() => toggleMember(email)}
                           data-testid={`${testid}-bulk-member-${(m.user_id || m.id)}`} />
                    <span className="font-medium">{m.name || email}</span>
                    <span className="text-meta">{email}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Additional emails (comma or space separated)</Label>
            <Input value={freeText} onChange={(e) => setFreeText(e.target.value)} placeholder="ceo@client.com, ops@partner.io" className="h-9 rounded-none text-xs" data-testid={`${testid}-bulk-free-emails`} />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-none text-xs" data-testid={`${testid}-bulk-subject`} />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Intro note (optional)</Label>
            <Textarea rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="Add context for the recipients…" className="rounded-none text-xs" data-testid={`${testid}-bulk-intro`} />
          </div>
          <div className="border border-border p-2 text-[11px] bg-muted/30 max-h-32 overflow-y-auto">
            <div className="font-semibold mb-1">Items included ({rows.length}):</div>
            <ul className="list-disc pl-4">
              {rows.map((r) => <li key={r.id}>[{r.kind}] {r.title} — <span className="text-meta">{r.brand_name || r.client_name || r.project_name || ""}</span></li>)}
            </ul>
          </div>
          <div className="text-[11px] text-meta">Recipients: {recipients.length}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid={`${testid}-bulk-cancel`}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || recipients.length === 0} data-testid={`${testid}-bulk-send`}>
            {busy ? "Sending…" : `Send to ${recipients.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
